/**
 * Anzeigeformate (Schweizer Schreibweise): Dezimalpunkt, Tausenderapostroph,
 * Datum 26.09.2026, Uhrzeit 14:30. Maschinenformate (CSV/JSON) nutzen diese
 * Funktionen nicht.
 */
const nfCache = new Map<number, Intl.NumberFormat>();

function nf(decimals: number): Intl.NumberFormat {
  let f = nfCache.get(decimals);
  if (!f) {
    f = new Intl.NumberFormat('de-CH', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    nfCache.set(decimals, f);
  }
  return f;
}

/** Zahl mit fester Stellenzahl; «–» für fehlende Werte. */
export function fmt(v: number | null | undefined, decimals = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  const s = nf(decimals).format(v === 0 ? 0 : v);
  return s.replace('-', '−');
}

export function fmtSigned(v: number | null | undefined, decimals = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  const s = fmt(Math.abs(v), decimals);
  if (Number(nf(decimals).format(Math.abs(v)).replace(/[’']/g, '')) === 0) return `±${s}`;
  return v > 0 ? `+${s}` : `−${s}`;
}

export function fmtPct(v: number | null | undefined, decimals = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '–';
  return `${fmtSigned(v * 100, decimals)} %`;
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '–';
  const [y, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.${y}`;
}

export function fmtDateShort(iso: string): string {
  const [, m, d] = iso.slice(0, 10).split('-');
  return `${d}.${m}.`;
}

export function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Lokale Wanduhrzeit «YYYY-MM-DDTHH:MM» → «26.09.2026, 14:30». */
export function fmtLocal(wall: string): string {
  return `${fmtDate(wall.slice(0, 10))}, ${wall.slice(11, 16)}`;
}

export function fmtCoord(v: number, decimals = 4): string {
  return v.toFixed(decimals);
}

export function fmtDuration(ms: number): string {
  if (ms <= 0) return 'wenige Sekunden';
  const min = Math.round(ms / 60_000);
  if (min < 1) return 'unter 1 Minute';
  if (min < 60) return `ca. ${min} Min.`;
  const h = Math.floor(min / 60);
  const rest = min % 60;
  if (h < 24) return rest ? `ca. ${h} h ${rest} Min.` : `ca. ${h} h`;
  const d = Math.floor(h / 24);
  return `ca. ${d} ${d === 1 ? 'Tag' : 'Tage'} ${h % 24} h`;
}

export const MONTHS = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
export const MONTHS_SHORT = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

export function hourLabel(h: number): string {
  return `${String(h).padStart(2, '0')}:00`;
}
